import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import PublishIcon from '@mui/icons-material/Publish';
import doRequest from '../../lib/services/apiRequestor';
import { downloadFile } from '../../lib/services/customQueryService';

export default function RowLevelFilterDialog({ idToken, open, onClose, filter }) {
    const [formData, setFormData] = useState({
        id: '',
        name: '',
        description: '',
        filterType: 'ManualInput',
        sourceView: '',
        sourceField: '',
        filterField: '',
        filterValues: '',
        items: []
    });
    const [isLoading, setIsLoading] = useState(false);
    const [newValue, setNewValue] = useState('');

    const mapFilterToFormData = (sourceFilter) => ({
        id: sourceFilter?.id || '',
        name: sourceFilter?.name || '',
        description: sourceFilter?.description || '',
        filterType: sourceFilter?.filterType || 'ManualInput',
        sourceView: sourceFilter?.sourceView || '',
        sourceField: sourceFilter?.sourceField || '',
        filterField: sourceFilter?.filterField || '',
        filterValues: sourceFilter?.filterValues || '',
        items: sourceFilter?.items || []
    });

    useEffect(() => {
        if (open && filter) {
            setFormData(mapFilterToFormData(filter));
        } else if (open) {
            setFormData(mapFilterToFormData(null));
        }
        setNewValue('');
    }, [open, filter]);

    const handleFieldChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAddItem = () => {
        if (newValue.trim()) {
            setFormData((prev) => ({
                ...prev,
                items: [...prev.items, newValue.trim()]
            }));
            setNewValue('');
        }
    };

    const handleRemoveItem = (index) => {
        setFormData((prev) => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const refreshFilter = async (filterId) => {
        const response = await doRequest(`/api/RowLevelFilters/${filterId}`, idToken);
        if (response.isError) {
            throw new Error(response.data || 'Failed to reload filter');
        }

        setFormData(mapFilterToFormData(response.data));
    };

    const handleExportCsv = async () => {
        if (!formData.id) {
            return;
        }

        setIsLoading(true);

        try {
            const response = await doRequest(`/api/RowLevelFilters/${formData.id}/items/export`, idToken, {
                method: 'GET'
            });

            if (response.isError) {
                throw new Error(response.data || 'Failed to export filter items');
            }

            downloadFile({
                content: response.data,
                fileName: `${formData.name || 'filter'}.csv`
            });
        } catch (error) {
            alert('Failed to export CSV: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleImportCsv = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!formData.id) {
            event.target.value = '';
            alert('Save the filter before importing CSV items.');
            return;
        }

        setIsLoading(true);

        try {
            const response = await doRequest(`/api/RowLevelFilters/${formData.id}/items/import`, idToken, {
                method: 'POST',
                headers: {
                    'content-type': file.type || 'text/plain'
                },
                body: file
            });

            if (response.isError) {
                throw new Error(response.data || 'Failed to import filter items');
            }

            await refreshFilter(formData.id);
        } catch (error) {
            alert('Failed to import CSV: ' + error.message);
        } finally {
            event.target.value = '';
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            alert('Name is required');
            return;
        }

        setIsLoading(true);
        const payload = {
            id: formData.id || undefined,
            name: formData.name,
            description: formData.description || null,
            filterType: formData.filterType,
            sourceView: formData.filterType === 'DatasetReference' ? formData.sourceView : null,
            sourceField: formData.filterType === 'DatasetReference' ? formData.sourceField : null,
            filterField: formData.filterType === 'DatasetReference' ? formData.filterField : null,
            filterValues: formData.filterType === 'DatasetReference' ? formData.filterValues : null,
            items: formData.filterType === 'ManualInput' ? formData.items : []
        };

        try {
            if (formData.id) {
                const response = await doRequest(`/api/RowLevelFilters/${formData.id}`, idToken, {
                    method: 'PUT',
                    headers: {
                        'content-type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });

                if (response.isError) {
                    throw new Error(response.data || 'Failed to update filter');
                }
            } else {
                const response = await doRequest('/api/RowLevelFilters', idToken, {
                    method: 'POST',
                    headers: {
                        'content-type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });

                if (response.isError) {
                    throw new Error(response.data || 'Failed to create filter');
                }
            }
            setIsLoading(false);
            onClose(true);
        } catch (error) {
            setIsLoading(false);
            alert('Failed to save filter: ' + error.message);
        }
    };

    return (
        <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
                {formData.id ? 'Edit Row Level Filter' : 'Create Row Level Filter'}
            </DialogTitle>
            <DialogContent dividers sx={{ pt: 2 }}>
                {isLoading && <CircularProgress />}
                {!isLoading && (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="Name"
                                fullWidth
                                value={formData.name}
                                onChange={(e) => handleFieldChange('name', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Description"
                                fullWidth
                                multiline
                                rows={2}
                                value={formData.description}
                                onChange={(e) => handleFieldChange('description', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Filter Type</InputLabel>
                                <Select
                                    value={formData.filterType}
                                    label="Filter Type"
                                    onChange={(e) => handleFieldChange('filterType', e.target.value)}
                                >
                                    <MenuItem value="ManualInput">Manual Input</MenuItem>
                                    <MenuItem value="DatasetReference">Dataset Reference</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {formData.filterType === 'ManualInput' && (
                            <>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <TextField
                                            label="Add Value"
                                            size="small"
                                            value={newValue}
                                            onChange={(e) => setNewValue(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddItem();
                                                }
                                            }}
                                            sx={{ flex: 1 }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleAddItem}
                                            size="small"
                                        >
                                            Add
                                        </Button>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                        <Button
                                            startIcon={<GetAppIcon />}
                                            onClick={handleExportCsv}
                                            size="small"
                                            disabled={!formData.id}
                                        >
                                            Export CSV
                                        </Button>
                                        <Button
                                            component="label"
                                            startIcon={<PublishIcon />}
                                            size="small"
                                            disabled={!formData.id}
                                        >
                                            Import CSV
                                            <input
                                                type="file"
                                                hidden
                                                accept=".csv,.txt"
                                                onChange={handleImportCsv}
                                            />
                                        </Button>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    {formData.items.length > 0 ? (
                                        <TableContainer component={Paper}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                        <TableCell>Value</TableCell>
                                                        <TableCell align="center" width="50px">
                                                            Action
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {formData.items.map((item, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{item}</TableCell>
                                                            <TableCell align="center">
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => handleRemoveItem(index)}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : (
                                        <Box sx={{ color: '#999', fontStyle: 'italic', p: 2 }}>
                                            No values added yet
                                        </Box>
                                    )}
                                </Grid>
                            </>
                        )}

                        {formData.filterType === 'DatasetReference' && (
                            <>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Source View"
                                        placeholder="e.g., [aladdin].[vw_AllowedPortfolios]"
                                        fullWidth
                                        value={formData.sourceView}
                                        onChange={(e) => handleFieldChange('sourceView', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Source Field"
                                        placeholder="e.g., PortfolioName"
                                        fullWidth
                                        value={formData.sourceField}
                                        onChange={(e) => handleFieldChange('sourceField', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Filter Field (optional)"
                                        placeholder="e.g., UserGroup"
                                        fullWidth
                                        value={formData.filterField}
                                        onChange={(e) => handleFieldChange('filterField', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Filter Values (optional)"
                                        placeholder="Comma-separated values to filter by"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        value={formData.filterValues}
                                        onChange={(e) => handleFieldChange('filterValues', e.target.value)}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose(false)}>Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={isLoading}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
